import React, { useCallback, useState } from 'react';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import { useToolkitConfig, ToolkitConfigStep, missingConfig } from './toolkit/ToolkitConfig';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Guest Ledger.
//
// Ported from the desktop toolkit's OHIP › Guest Ledger: each balance in an Opera v5 guest ledger export (delimited)
// is posted to its Opera Cloud reservation as a charge on window 1, on GL_BAL_FORWARD_TRX_CODE, through the project's
// own connection (toolkit ledgers.js post-guest-charge). The desktop's rules:
//
//   CHECKED IN / IN HOUSE — posted when the Cloud reservation is checked in too;
//   RESERVED              — only with "Post pre-stay charges" (pre-stay billing must be enabled in OPERA);
//   CHECKED OUT           — only with "Process open folios": a zero-rate reservation is created in Opera Cloud for the
//                           guest, given a vacant room of the chosen type and checked in, then posted to — and reused
//                           on any later run.
//
// NEVER TWICE: a row this tool already posted is skipped, and so is one whose amount is already on the folio on the
// same transaction code — that one can be posted anyway, deliberately, from its row.
//
// THE ROW KEY is the confirmation number, the balance and how many times that pair has appeared above it in the file
// — stable when the file is re-exported in another order. (The desktop used the row's position in the file, so a
// re-export in a different order re-posted some charges and skipped others.)

const COLUMNS = [
    { key: 'CONFIRMATION_NO', label: 'Confirmation' },
    { key: 'ROOM', label: 'Room' },
    { key: 'guestName', label: 'Guest' },
    { key: 'RESV_STATUS', label: 'v5 status' },
    { key: 'BALANCE', label: 'Balance (v5)' },
    { key: 'postedAmount', label: 'Posted' },
];
const SEARCH_KEYS = ['CONFIRMATION_NO', 'ROOM', 'guestName', 'RESV_STATUS'];
const ROW_ACTIONS = [{ label: 'Post anyway', title: 'The folio already has this amount on this transaction code — post it again',
    show: (r) => r.alreadyInCloud, opts: { forcePost: true } }];

const OPEN_FOLIO_DEFAULTS = { rateCode: 'HOUSE', guaranteeCode: 'COM', marketCode: 'BUSINESS', sourceCode: 'PHONE',
    roomTypeCode: 'PM', paymentMethod: 'CA', numberOfAdults: 0, numberOfChildren: 0, stayDuration: 7 };
const OPEN_FOLIO_FIELDS = [
    ['rateCode', 'Rate code'], ['guaranteeCode', 'Guarantee'], ['marketCode', 'Market'], ['sourceCode', 'Source'],
    ['roomTypeCode', 'Room type'], ['paymentMethod', 'Payment method'],
    ['numberOfAdults', 'Adults', true], ['numberOfChildren', 'Children', true], ['stayDuration', 'Nights', true],
];

const withRowKeys = (rows) => {
    const seen = {};
    return rows.map((r) => {
        const pair = `${r.CONFIRMATION_NO}|${r.BALANCE}`;
        seen[pair] = (seen[pair] || 0) + 1;
        return { ...r, rowKey: `${pair}|${seen[pair]}`, guestName: r.FULL_NAME || r.SGUEST_NAME || '' };
    });
};

function ToolkitGuestLedgerPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const config = useToolkitConfig(ctx, { transactionCodeKind: 'guest-ledger-transaction-code' });
    const [postingReference, setPostingReference] = useState('Balance Transferred');
    const [preStay, setPreStay] = useState(false);
    const [openFolios, setOpenFolios] = useState(false);
    const [folio, setFolio] = useState(OPEN_FOLIO_DEFAULTS);
    const missing = missingConfig(config);

    // On the server a row is never force-posted: "Post anyway" is the row's own button, here.
    const request = useCallback((row, opts = {}) => ({ area: 'ledger', op: 'post-guest-charge', args: {
        v5Record: sourceRow(row, ['rowKey', 'guestName', 'postedAmount', 'alreadyInCloud']),
        rowKey: row.rowKey,
        cashierId: config.cashierId, currencyCode: config.currencyCode, transactionCode: config.transactionCode,
        postingReference, preStayEnabled: preStay, forcePost: !!opts.forcePost,
        openFolio: openFolios ? folio : null,
        amountOverride: config.convertAmount(row.BALANCE),
    } }), [config, postingReference, preStay, openFolios, folio]);
    const interpret = useCallback((r, row) => ({ status: r.status, errorMessage: r.errorMessage || null, alreadyInCloud: !!r.alreadyInCloud,
        postedAmount: r.postedAmount ? `${r.postedAmount} ${r.currencyCode}` : row.postedAmount }), []);

    const batch = useToolkitBatch({ request, interpret, ctx,
        server: { screen: 'guest-ledger', title: 'Guest Ledger', keyOf: (row) => row.rowKey } });
    const checkedOut = batch.rows.filter((r) => String(r.RESV_STATUS || '').toUpperCase().replace(/\s+/g, '') === 'CHECKEDOUT').length;
    const reserved = batch.rows.filter((r) => String(r.RESV_STATUS || '').toUpperCase().replace(/\s+/g, '') === 'RESERVED').length;

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Guest Ledger"
                subtitle="Post the open balances of an Opera v5 guest ledger export to their Opera Cloud reservations as charges." />

            <ToolkitConfigStep config={config} number={1} ready={ready}>
                <label><span>Posting reference</span><input type="text" value={postingReference} onChange={(e) => setPostingReference(e.target.value)} style={{ width: 180 }} /></label>
            </ToolkitConfigStep>

            <ToolkitFileStep number={2} title="Opera v5 guest ledger" label="Choose the guest ledger export (CSV / pipe)"
                accept=".csv,.txt,.rep" kind="guest-ledger" onParsed={(rows) => batch.load(withRowKeys(rows))} disabled={!ready}>
                {!!batch.rows.length && (
                    <p className="tkb-hint">
                        {checkedOut} checked out (open folios) and {reserved} reserved (pre-stay) of {batch.rows.length} row(s).
                        {missing.length > 0 && ` Posting needs ${missing.join(', ')}.`}
                    </p>
                )}
            </ToolkitFileStep>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">3</span> Options</div>
                <div className="tkb-row">
                    <label className="tkb-check">
                        <input type="checkbox" checked={preStay} onChange={(e) => setPreStay(e.target.checked)} />
                        <span>Post pre-stay charges (RESERVED)</span>
                    </label>
                    <label className="tkb-check">
                        <input type="checkbox" checked={openFolios} onChange={(e) => setOpenFolios(e.target.checked)} />
                        <span>Process open folios (CHECKED OUT)</span>
                    </label>
                </div>
                {preStay && <p className="tkb-hint">Pre-stay billing must be enabled in Opera Cloud; the reservation must still be Reserved there.</p>}
                {openFolios && (
                    <>
                        <p className="tkb-hint">
                            Each checked-out guest gets a zero-rate reservation in Opera Cloud from today's business date, a vacant
                            room of this type and a check-in — then the balance is posted to it. It is reused on later runs.
                        </p>
                        <div className="tkb-row">
                            {OPEN_FOLIO_FIELDS.map(([k, label, numeric]) => (
                                <label key={k}>
                                    <span>{label}</span>
                                    <input type={numeric ? 'number' : 'text'} min={numeric ? 0 : undefined} value={folio[k]}
                                        style={{ width: numeric ? 80 : 120 }}
                                        onChange={(e) => setFolio((f) => ({ ...f, [k]: numeric ? Number(e.target.value || 0) : e.target.value.toUpperCase() }))} />
                                </label>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={SEARCH_KEYS} exportName="Guest_Ledger"
                canRun={ready && !missing.length} onClear={() => batch.load([])} rowActions={ROW_ACTIONS} />
        </div>
    );
}

export default ToolkitGuestLedgerPage;
